import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API, auth, money, fmtDate, inputCls, Field, Modal, PageHead, PrimaryBtn, FinanceEmpty, StatusBadge, Paginator, exportExcel, EXPENSE_CATS, REVENUE_CATS, PAY_METHODS, STATUS_LABELS, errMsg } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';
import PremiumDatePicker from '../../../components/ui/PremiumDatePicker';

const PAGE_SIZE = 15;

const CONF = {
  expense: { title: 'المصروفات', desc: 'تسجيل واعتماد مصروفات المنشأة', ep: 'expenses', dateField: 'expense_date', partyField: 'vendor_name', partyLabel: 'المستفيد / المورد', cats: EXPENSE_CATS },
  revenue: { title: 'الإيرادات', desc: 'تسجيل إيرادات المنشأة ومصادر الدخل', ep: 'revenues', dateField: 'revenue_date', partyField: 'customer_name', partyLabel: 'الجهة / العميل', cats: REVENUE_CATS },
};

export default function TransactionsPage({ kind }) {
  const c = CONF[kind];
  const { user, selectedSeasonId } = useOutletContext() || {};
  const seasonQS = selectedSeasonId && selectedSeasonId !== 'all' ? `?season_id=${selectedSeasonId}` : '';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [toast, setToast] = useState(null);

  const canApprove = user?.is_super_admin || ['admin', 'manager'].includes(user?.role);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/${c.ep}${seasonQS}`, auth());
      setItems(res.data || []);
    } catch (e) {
      setError(e.response?.status === 403 ? 'لا تملك صلاحية الوصول للبيانات المالية' : 'تعذر التحميل');
    }
    setLoading(false);
  }, [c.ep, seasonQS]);
  useEffect(() => { load(); }, [load]);

  const openForm = (item) => {
    setEditing(item || null);
    setForm(item ? { ...item } : { title: '', amount: '', category: 'other', payment_method: 'bank_transfer', [c.dateField]: '', [c.partyField]: '', notes: '' });
    setFormError(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.amount || !form[c.dateField]) { setFormError('يرجى تعبئة العنوان والمبلغ والتاريخ'); return; }
    setSaving(true); setFormError(null);
    const payload = { title: form.title, amount: parseFloat(form.amount), category: form.category, payment_method: form.payment_method, [c.dateField]: form[c.dateField], notes: form.notes || undefined };
    if (form[c.partyField]) payload[c.partyField] = form[c.partyField];
    try {
      if (editing) await axios.put(`${API}/${c.ep}/${editing.id}`, payload, auth());
      else await axios.post(`${API}/${c.ep}`, payload, auth());
      setModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (err) { setFormError(errMsg(err, 'فشل الحفظ')); }
    setSaving(false);
  };

  const setStatus = async (item, status) => {
    try { await axios.put(`${API}/expenses/${item.id}`, { status }, auth()); notify(`تم التحديث إلى: ${STATUS_LABELS[status]}`); load(); }
    catch (err) { notify(errMsg(err, 'فشلت العملية')); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/${c.ep}/${confirmDel.id}${voidReason ? `?reason=${encodeURIComponent(voidReason)}` : ''}`, auth());
      notify('تم إلغاء العملية — بقيت في السجل للتدقيق'); setConfirmDel(null); setVoidReason(''); load();
    }
    catch (err) { notify(errMsg(err, 'فشل الإلغاء')); }
  };

  const filtered = items
    .filter((x) => !search.trim() || x.title?.includes(search.trim()) || (x[c.partyField] || '').includes(search.trim()))
    .filter((x) => !catFilter || x.category === catFilter)
    .filter((x) => !statusFilter || x.status === statusFilter)
    .sort((a, b) => new Date(b[c.dateField]) - new Date(a[c.dateField]));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total = filtered.reduce((s, x) => s + (kind === 'expense' && x.status === 'rejected' ? 0 : x.amount), 0);

  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="tx-error">{error}</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid={`${kind}s-page`}>
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title={c.title} desc={c.desc} action={
        <div className="flex gap-2">
          <button onClick={() => exportExcel(filtered.map((x) => ({ 'العنوان': x.title, 'المبلغ': x.amount, 'التصنيف': c.cats[x.category], 'التاريخ': x[c.dateField], 'الحالة': STATUS_LABELS[x.status] || '', [c.partyLabel]: x[c.partyField] || '' })), c.title)}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" data-testid="tx-export-btn">
            <Icon name="chart" className="w-4 h-4" /> Excel
          </button>
          <PrimaryBtn onClick={() => openForm(null)} data-testid="tx-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة</PrimaryBtn>
        </div>} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">عدد السجلات</div><div className="text-xl font-bold text-slate-900" data-testid="tx-count">{filtered.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">الإجمالي</div><div className={`text-xl font-bold ${kind === 'expense' ? 'text-red-600' : 'text-teal-700'}`} dir="rtl" data-testid="tx-total">{money(total)}</div></div>
        {kind === 'expense' && <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">بانتظار الاعتماد</div><div className="text-xl font-bold text-amber-600">{items.filter((x) => x.status === 'pending').length}</div></div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="بحث..." className="w-36 bg-transparent text-sm outline-none" data-testid="tx-search" />
          </div>
          <select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-600" data-testid="tx-cat-filter">
            <option value="">كل التصنيفات</option>
            {Object.entries(c.cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {kind === 'expense' && (
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-600" data-testid="tx-status-filter">
              <option value="">كل الحالات</option>
              {['pending', 'approved', 'paid', 'rejected'].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          )}
        </div>

        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : paged.length === 0 ? <div className="p-6"><FinanceEmpty text={items.length === 0 ? `لا توجد ${c.title} مسجلة بعد` : 'لا توجد نتائج مطابقة'} /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">البيان</th>
                  <th className="px-4 py-2.5 font-semibold">المبلغ</th>
                  <th className="hidden px-4 py-2.5 font-semibold md:table-cell">التصنيف</th>
                  <th className="hidden px-4 py-2.5 font-semibold lg:table-cell">التاريخ</th>
                  {kind === 'expense' && <th className="px-4 py-2.5 font-semibold">الحالة</th>}
                  <th className="px-4 py-2.5 font-semibold">إجراءات</th>
                </tr></thead>
                <tbody>
                  {paged.map((x) => (
                    <tr key={x.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="tx-row">
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-slate-800">{x.title}</div>
                        {x[c.partyField] && <div className="text-xs text-slate-500">{x[c.partyField]}</div>}
                      </td>
                      <td className={`px-4 py-2.5 font-bold ${kind === 'expense' ? 'text-red-600' : 'text-teal-700'}`} dir="rtl">{money(x.amount)}</td>
                      <td className="hidden px-4 py-2.5 md:table-cell"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{c.cats[x.category] || x.category}</span></td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(x[c.dateField])}</td>
                      {kind === 'expense' && <td className="px-4 py-2.5"><StatusBadge status={x.status} /></td>}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          {kind === 'expense' && canApprove && x.status === 'pending' && (<>
                            <button onClick={() => setStatus(x, 'approved')} title="اعتماد" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="tx-approve-btn"><Icon name="check" className="w-4 h-4" /></button>
                            <button onClick={() => setStatus(x, 'rejected')} title="رفض" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="tx-reject-btn"><Icon name="x" className="w-4 h-4" /></button>
                          </>)}
                          {kind === 'expense' && canApprove && x.status === 'approved' && (
                            <button onClick={() => setStatus(x, 'paid')} title="تحويل إلى مدفوع" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="tx-pay-btn"><Icon name="banknotes" className="w-4 h-4" /></button>
                          )}
                          <button onClick={() => openForm(x)} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="tx-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                          <button onClick={() => setConfirmDel(x)} title="إلغاء العملية" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="tx-del-btn"><Icon name="x" className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <Paginator page={page} total={filtered.length} limit={PAGE_SIZE} onChange={setPage} />
      </div>

      <Modal open={modal} title={editing ? `تعديل ${c.title}` : `إضافة ${c.title}`} onClose={() => setModal(false)} wide>
        {formError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="tx-form-error">{formError}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="البيان / العنوان" required><input className={inputCls} value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tx-input-title" /></Field>
          <Field label="المبلغ (ر.س)" required><input className={inputCls} dir="ltr" type="number" min="0.01" step="0.01" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tx-input-amount" /></Field>
          <Field label="التصنيف"><select className={inputCls} value={form.category || 'other'} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="tx-input-category">{Object.entries(c.cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="طريقة الدفع"><select className={inputCls} value={form.payment_method || 'bank_transfer'} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>{Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="التاريخ" required><PremiumDatePicker value={form[c.dateField] || ''} onChange={(v) => setForm({ ...form, [c.dateField]: v })} testId="tx-input-date" /></Field>
          <Field label={c.partyLabel}><input className={inputCls} value={form[c.partyField] || ''} onChange={(e) => setForm({ ...form, [c.partyField]: e.target.value })} /></Field>
          <Field label="ملاحظات"><input className={inputCls} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="col-span-full mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={saving} data-testid="tx-form-submit">{saving ? 'جارٍ الحفظ...' : 'حفظ'}</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="إلغاء العملية" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">سيتم إلغاء «{confirmDel?.title}» بدل حذفها نهائياً — تبقى في سجل التدقيق المالي.</p>
        <Field label="سبب الإلغاء (اختياري)"><input className={inputCls} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="tx-void-reason" /></Field>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700" data-testid="tx-confirm-del">تأكيد الإلغاء</button>
        </div>
      </Modal>
    </div>
  );
}
